import { array_from_query, number_from_query, str_from_query } from "@/api";
import { axios_instance } from "@/api/api";
import { type version } from "@/components/VersionControl";
import { router } from "@/router/router";
import { useQuery, type QueryFunctionContext } from "@tanstack/vue-query";
import { computed, reactive, ref, watch } from "vue";

interface VersionFilters {
  keyword: string;
  type: string[];
  page: number;
  size: number;
}

type VersionsQueryKey = ["versions", VersionFilters];

const fetchVersions = async (
  context: QueryFunctionContext<VersionsQueryKey>,
): Promise<{ data: version[] | null; total_filter: number }> => {
  const [, filters] = context.queryKey;

  const data = JSON.stringify({
    keyword: filters.keyword,
    type: filters.type,
    page: filters.page,
    size: filters.size,
  });

  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: "/api/v1/view/version/pagination",
    data: data,
  };

  const response = await axios_instance.request<{
    data: version[] | null;
    total_filter: number;
  }>(config);

  return response.data;
};

export function useVersionsQuery() {
  const filters = reactive<VersionFilters>({
    keyword: str_from_query("keyword") || "",
    get type() {
      return array_from_query("type", String);
    },
    page: number_from_query("page") ?? 1,
    size: number_from_query("size") ?? 24,
  });

  watch(
    () => router.currentRoute.value.query,
    () => {
      Object.assign(filters, {
        keyword: str_from_query("keyword") || "",
        page: number_from_query("page") ?? 1,
        size: number_from_query("size") ?? 24,
      });
    },
  );

  const queryKey = ref<VersionsQueryKey>(["versions", filters]);

  watch(
    filters,
    (newFilters) => {
      queryKey.value = ["versions", newFilters];
    },
    { deep: true },
  );

  const queryResult = useQuery<
    {
      data: version[] | null;
      total_filter: number;
    },
    Error,
    {
      data: version[] | null;
      total_filter: number;
    },
    VersionsQueryKey
  >({
    queryKey: queryKey,
    queryFn: fetchVersions,
    staleTime: 5 * 60 * 1000,
    // ponytail: always enabled — empty type shows all versions; depends on
    // backend /version/pagination accepting type: [].
    enabled: true,
  });

  const totalPages = computed(() => {
    const totalItems = queryResult.data.value?.total_filter || 0;
    const pageSize = filters.size || 20;

    if (totalItems === 0) {
      return 0;
    }

    return Math.ceil(totalItems / pageSize);
  });

  return {
    ...queryResult,
    filters,
    totalPages,
  };
}
